import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaCheckCircle, FaShoppingCart, FaCrown, FaRedo, FaCalendarCheck } from 'react-icons/fa';
import { Elements, PaymentElement } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../reducers/authSlice';
import { RootState } from '../store/store';
import api from '../api/axiosInstance';
import { errText, httpStatus } from '../api/errors';
import { SubscriptionCheckout, checkoutState, newCheckoutAttemptId, paymentAmount } from './subscriptionCheckout';

// Both purchase types confirm a PaymentIntent for the actual payable amount.
interface PaymentFormProps {
    returnUrl: string;
    amount: number;
    currency: string;
    recurring: boolean;
    /**
     * Where a declined card goes. Without it the failure only ever reached the console: the button
     * un-greyed itself, the modal stayed exactly as it was, and the member was left looking at a
     * form that had just silently refused them. Stripe's own message is the useful one here — it is
     * localised and it says which of "insufficient funds", "expired card" or "declined" happened,
     * which is more than this component could work out for itself.
     */
    onError: (message: string) => void;
}

export const CheckoutPaymentForm: React.FC<PaymentFormProps> = ({ returnUrl, amount, currency, recurring, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const processing = useRef(false);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const handlePaymentSubmit = async () => {
        if (!stripe || !elements || processing.current) return;

        processing.current = true;
        setIsProcessing(true);
        onError('');
        try {
            const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
            });

            // A successful confirmation navigates to return_url; immediate failures stay here.
            if (error && mounted.current) {
                onError(error.message || 'Platbu sa nepodarilo dokončiť. Skúste to znova alebo použite inú kartu.');
            }
        } catch {
            if (mounted.current) onError('Nepodarilo sa overiť výsledok platby. Skontrolujte svoje členstvo pred ďalším pokusom.');
        } finally {
            processing.current = false;
            if (mounted.current) setIsProcessing(false);
        }
    };

    return (
        <>
            <CenteredPrice>Teraz zaplatíte {paymentAmount(amount, currency)}</CenteredPrice>
            {recurring && <InfoBox>Členstvo sa automaticky obnovuje každý mesiac. Obnovovanie môžete zrušiť v časti Členstvo.</InfoBox>}
            <PaymentElement />
            <Button
                onClick={handlePaymentSubmit}
                disabled={!stripe || !elements || isProcessing}
                style={{ marginTop: '20px', width: '100%' }}
            >
                {isProcessing ? 'Spracováva sa...' : `Zaplatiť ${paymentAmount(amount, currency)}`}
            </Button>
        </>
    );
};

interface SubscriptionProduct {
    id: number;
    name: string;
    price: number;
    icon: string;
    durationDays: number;
    isRecurring: boolean;
}

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const Modal = styled.div`
    background: white;
    width: 700px;
    max-width: 100%;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    background-color: #1C1C1E;
    overflow-y: auto;
`;

const ModalTitle = styled.h2`
    font-size: 1.5em;
    margin: 0;
    display: flex;
    align-items: center;
    color: #f56607;
`;

const PriceText = styled.div`
    font-size: 0.9em;
    color: white;
`;

const StepNav = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const StepItem = styled.div<{ active: boolean }>`
    display: flex;
    align-items: center;
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
    color: ${(props) => (props.active ? '#FF7F00' : '#ccc')};
    position: relative;
`;

const CheckIcon = styled(FaCheckCircle)`
    color: #FF7F00;
    margin-right: 8px;
`;

const ModalContent = styled.div`
    margin-bottom: 20px;
    color: white;
    overflow-y: auto;
    max-height: 60vh;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Button = styled.button`
    background-color: #FF7F00;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #e1860f;
    }

    &:disabled {
        background-color: #666;
        cursor: not-allowed;
    }
`;

const ModalStep = styled.div`
    border-top: 1px solid #ddd;
    padding: 10px 0;
`;

const StepTitle = styled.h3`
    font-size: 1.2em;
    margin: 10px 0;
    color: #f56607;
`;

const ProductImage = styled.img`
    width: 100px;
    height: 100px;
    border-radius: 8px;
    background-color: #e0e0e0;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: #ccc;
    margin: 20px 0;
`;

const Input = styled.input`
    margin: 10px 0;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #f56607;
    background-color: #333;
    color: white;
`;

const ErrorMessage = styled.label`
    margin-top: 20px;
    font-weight: bold;
    display: block;
    text-align: center;
    color: red;
`;

interface ModalComponentProps {
    isOpen: boolean;
    onClose: () => void;
    stripePromise: Promise<Stripe | null>;
}

const CenteredContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-top: 20px;
`;

const CenteredText = styled.p`
    font-size: 18px;
    font-weight: bold;
    color: #f56607;
    margin: 10px 0;
`;

const CenteredPrice = styled.p`
    font-size: 20px;
    font-weight: bold;
    color: #ffffff;
    margin-top: 5px;
`;

// Package selection styles
const PackagesSection = styled.div`
    margin: 20px 0;
`;

const SectionTitle = styled.h4`
    color: #f56607;
    font-size: 1em;
    margin: 20px 0 10px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #3C3C3E;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionSubtitle = styled.p`
    color: #888;
    font-size: 0.8em;
    margin: 0 0 15px 0;
`;

const PackagesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin: 10px 0;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const SubscriptionContainer = styled.div`
    margin: 10px 0;
`;

// `selected` and nothing else. An `isSubscription` prop used to be declared here and passed in
// from the recurring card; nothing in the CSS below ever read it, so styled-components forwarded it
// to the underlying <div> and React logged "unknown prop isSubscription on a DOM element" for every
// card rendered. Removed from both ends rather than transient-renamed: there is no style to keep.
const PackageCard = styled.div<{ selected: boolean }>`
    background: #2C2C2E;
    border: ${(props) => (props.selected ? '3px solid #FF7F00' : '2px solid #3C3C3E')};
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: #FF7F00;
        transform: translateY(-2px);
    }

`;

const PackageName = styled.h4`
    color: white;
    margin: 0 0 8px 0;
    font-size: 1em;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PackagePrice = styled.div`
    color: #f56607;
    font-size: 1.4em;
    font-weight: bold;
    margin-bottom: 4px;
`;

const PackageDetails = styled.div`
    color: #aaa;
    font-size: 0.8em;
`;

const SubscriptionBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(88, 86, 214, 0.2);
    border-radius: 6px;
    font-size: 0.75em;
    color: #A5A4F3;
`;

const OneTimeBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    font-size: 0.75em;
    color: #81C784;
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    color: #f56607;
`;

const InfoBox = styled.div`
    background: rgba(255, 127, 0, 0.1);
    border: 1px solid rgba(255, 127, 0, 0.3);
    border-radius: 8px;
    padding: 12px;
    margin-top: 15px;
    font-size: 0.85em;
    color: #ccc;

    strong {
        color: #f56607;
    }
`;

const BuyPremium: React.FC<ModalComponentProps> = ({ isOpen, onClose, stripePromise }) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const theFullName = user ? user.fullName : '';
    const userId = user ? user.id : null;
    const token = useSelector((state: RootState) => state.auth.accessToken);
    const requestVersion = useRef(0);
    const advancing = useRef(false);
    const attemptId = useRef<string | null>(null);

    // Package selection state
    const [packages, setPackages] = useState<SubscriptionProduct[]>([]);
    const [selectedPackage, setSelectedPackage] = useState<SubscriptionProduct | null>(null);
    const [loadingPackages, setLoadingPackages] = useState(true);

    const [fullName, setFullName] = useState(theFullName);
    const [currentStep, setCurrentStep] = useState(0);
    const [streetAddress, setStreetAddress] = useState('');
    const [postcode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');

    const [showMissingFieldsMessage, setShowMissingFieldsMessage] = useState(false);

    /**
     * Whatever went wrong last, in Slovak, shown inside the modal.
     *
     * Both failures this covers used to be a `console.error` and nothing else: a catalogue that
     * would not load left the buyer staring at an empty package list with "Ďalej" disabled, and a
     * PaymentIntent that could not be created still advanced the wizard to a payment step that
     * could never complete.
     */
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // For one-time payments: store the PaymentIntent clientSecret
    const [oneTimeClientSecret, setOneTimeClientSecret] = useState<string | null>(null);
    const [oneTimeAmount, setOneTimeAmount] = useState(0);
    const [checkout, setCheckout] = useState<SubscriptionCheckout | null>(null);
    const [loadingPaymentIntent, setLoadingPaymentIntent] = useState(false);

    useEffect(() => {
        requestVersion.current += 1;
        advancing.current = false;
        setCurrentStep(0);
        setCheckout(null);
        setOneTimeClientSecret(null);
        setLoadingPaymentIntent(false);
        setErrorMessage(null);
        attemptId.current = null;
        return () => { requestVersion.current += 1; };
    }, [isOpen, userId, token]);

    const [missingFields, setMissingFields] = useState({
        fullName: false,
        streetAddress: false,
        postcode: false,
        city: false,
        country: false,
    });

    // Fetch available subscription packages
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                setLoadingPackages(true);
                setErrorMessage(null);
                const response = await api.get('/products/subscriptions');
                const products = response.data as SubscriptionProduct[];
                setPackages(products);
                // The recurring plan is the product on sale, so it is what the wizard opens on.
                // Selecting by `durationDays === 30` was the same row by coincidence, and would
                // have silently picked a retired 30-day one-off pass over the subscription.
                const defaultPackage = products.find((p) => p.isRecurring) || products[0];
                if (defaultPackage) {
                    setSelectedPackage(defaultPackage);
                }
            } catch (error) {
                // Never log the error object itself: axios hangs the request config off it, and
                // that carries the bearer token.
                setErrorMessage(errText(error, 'Balíčky sa nepodarilo načítať. Skúste to znova.'));
            } finally {
                setLoadingPackages(false);
            }
        };
        if (isOpen) {
            fetchPackages();
        }
    }, [isOpen]);

    useEffect(() => {
        if (userId) {
            const fetchBillingDetails = async () => {
                try {
                    const response = await api.get(`/account/${userId}`);
                    const { fullName, streetAddress, zipcode, city, country } = response.data;
                    setFullName(fullName || '');
                    setStreetAddress(streetAddress || '');
                    setPostcode(zipcode || '');
                    setCity(city || '');
                    setCountry(country || '');
                } catch (error) {
                    // Non-fatal: the fields stay empty and step 1 makes the buyer fill them in.
                    // Only the message is logged — the axios error carries the bearer token.
                    console.warn('Could not prefill billing details:', errText(error, 'unknown error'));
                }
            };
            fetchBillingDetails();
        }
    }, [userId]);

    const handleNextStep = async () => {
        if (advancing.current || !validateStep()) return;
        advancing.current = true;
        const version = requestVersion.current;
        setLoadingPaymentIntent(true);
        setErrorMessage(null);
        try {
            if (currentStep === 1) {
                await api.put(`/account/${userId}`, {
                    fullname: fullName,
                    streetAddress,
                    zipcode: postcode,
                    city,
                    country,
                });
            }

            // Prepare the actual charge only when the buyer reaches the payment step.
            if (currentStep === 2 && selectedPackage) {
                if (selectedPackage.isRecurring) {
                    if (!attemptId.current) attemptId.current = newCheckoutAttemptId();
                    const response = await api.post<SubscriptionCheckout>(`/subscription-checkout/${userId}`, {
                        productId: selectedPackage.id,
                        attemptId: attemptId.current,
                    });
                    if (version !== requestVersion.current) return;
                    const state = checkoutState(response.data);
                    if (state === 'unavailable') {
                        setErrorMessage('Platobný formulár sa nepodarilo pripraviť. Skontrolujte svoje členstvo alebo kontaktujte podporu.');
                        return;
                    }
                    setCheckout(response.data);
                    if (state === 'existing' || state === 'paid') {
                        // Repair the local access mirror after a missed webhook, without starting
                        // another purchase or trusting the browser to declare the member premium.
                        try {
                            const account = await api.put(`/account/premium/${userId}?productId=${selectedPackage.id}`, {});
                            if (version !== requestVersion.current) return;
                            dispatch(updateUser(account.data));
                        } catch (error) {
                            console.warn('Členstvo sa ešte nepodarilo obnoviť:', httpStatus(error));
                        }
                    }
                } else {
                    const response = await api.post(`/payment-intent/${userId}?productId=${selectedPackage.id}`);
                    if (version !== requestVersion.current) return;
                    const { clientSecret, amount, isOneTime } = response.data;
                    if (!isOneTime || typeof clientSecret !== 'string' || !clientSecret.startsWith('pi_')
                        || !Number.isSafeInteger(amount) || amount <= 0) {
                        setErrorMessage('Platobný formulár sa nepodarilo pripraviť. Skúste to znova alebo kontaktujte podporu.');
                        return;
                    }
                    setOneTimeClientSecret(clientSecret);
                    setOneTimeAmount(amount);
                }
            }
            if (version === requestVersion.current) setCurrentStep(currentStep + 1);
        } catch (error) {
            if (version === requestVersion.current) {
                setErrorMessage(errText(error, currentStep === 1
                    ? 'Fakturačné údaje sa nepodarilo uložiť. Skúste to znova.'
                    : 'Platbu sa nepodarilo pripraviť. Skúste to znova.'));
            }
        } finally {
            if (version === requestVersion.current) {
                advancing.current = false;
                setLoadingPaymentIntent(false);
            }
        }
    };

    const handlePreviousStep = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const validateStep = () => {
        if (currentStep === 0) {
            // Validate package selection
            return selectedPackage !== null;
        }
        if (currentStep === 1) {
            const newMissingFields = {
                fullName: fullName.trim() === '',
                streetAddress: streetAddress.trim() === '',
                postcode: postcode.trim() === '',
                city: city.trim() === '',
                country: country.trim() === '',
            };

            setMissingFields(newMissingFields);
            const hasMissingFields = Object.values(newMissingFields).includes(true);
            setShowMissingFieldsMessage(hasMissingFields);
            return !hasMissingFields;
        }
        return true;
    };

    /**
     * How long a one-off pass lasts, in Slovak.
     *
     * The 1 Deň / 30 Dní / 3 Mesiace / 1 Rok lookup table this replaced described a catalogue that
     * no longer exists — and its companion `getBillingPeriod` (Mesačne / Štvrťročne / Ročne) was
     * never rendered at all. Only a retired pass an admin has put back on sale reaches this
     * function, so a correctly declined day count beats a table of products nobody can buy.
     */
    const formatDuration = (days: number): string => {
        if (days === 30) return '1 mesiac';
        if (days === 1) return '1 deň';
        return days >= 2 && days <= 4 ? `${days} dni` : `${days} dní`;
    };

    if (!isOpen) return null;

    const totalPrice = currentStep === 3
        ? (selectedPackage?.isRecurring ? checkout?.amount ?? 0 : oneTimeAmount) / 100
        : selectedPackage?.price || 0;

    /**
     * Whether the catalogue still holds a one-off pass.
     *
     * After the monthly-only cutover `GET /products/subscriptions` returns exactly the recurring
     * plan, so this is false and the whole "Jednorazové balíčky" half of the step disappears —
     * the wizard's first step becomes a single-card confirmation. It stays conditional rather than
     * deleted because the retired rows are only hidden (`is_subscription = false`), and an admin
     * putting one back on sale must not meet a screen that cannot show it.
     */
    const hasOneTimePackages = packages.some((pkg) => !pkg.isRecurring);

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                    <FaShoppingCart />
                    Košík
                </ModalTitle>
                {(currentStep < 3 || !selectedPackage?.isRecurring || (checkout && checkoutState(checkout) === 'ready'))
                    && totalPrice > 0 && <PriceText>{totalPrice.toFixed(2)} EUR</PriceText>}
                <StepNav>
                    {['Balíček', 'Fakturačné Údaje', 'Rekapitulácia', 'Platba'].map((step, index) => (
                        <StepItem key={index} active={currentStep === index}>
                            {currentStep >= index ? <CheckIcon /> : null}
                            {step}
                        </StepItem>
                    ))}
                </StepNav>
                <ModalContent>
                    {currentStep === 0 && (
                        <ModalStep>
                            <StepTitle>Vyberte si balíček</StepTitle>
                            {loadingPackages ? (
                                <LoadingSpinner>Načítavam balíčky...</LoadingSpinner>
                            ) : (
                                <PackagesSection>
                                    {/* One-time payment packages. Retired from sale — rendered only
                                        while the catalogue still returns one. */}
                                    {hasOneTimePackages && (
                                        <>
                                            <SectionTitle>
                                                <FaCalendarCheck color="#4CAF50" />
                                                Jednorazové balíčky
                                            </SectionTitle>
                                            <SectionSubtitle>
                                                Zaplaťte raz a užívajte si prístup po vybranú dobu. Bez automatického obnovovania.
                                            </SectionSubtitle>
                                            <PackagesContainer>
                                                {packages
                                                    .filter((pkg) => !pkg.isRecurring)
                                                    .map((pkg) => (
                                                        <PackageCard
                                                            key={pkg.id}
                                                            selected={selectedPackage?.id === pkg.id}
                                                            onClick={() => setSelectedPackage(pkg)}
                                                        >
                                                            <PackageName>
                                                                <FaCrown color="#FFD700" />
                                                                {pkg.name}
                                                            </PackageName>
                                                            <PackagePrice>{pkg.price.toFixed(2)} €</PackagePrice>
                                                            <PackageDetails>
                                                                {formatDuration(pkg.durationDays)} prístupu
                                                            </PackageDetails>
                                                            <OneTimeBadge>
                                                                <FaCalendarCheck />
                                                                Jednorazová platba
                                                            </OneTimeBadge>
                                                        </PackageCard>
                                                    ))}
                                            </PackagesContainer>
                                        </>
                                    )}

                                    {/* Subscription packages */}
                                    <SectionTitle>
                                        <FaRedo color="#5856D6" />
                                        Predplatné
                                    </SectionTitle>
                                    <SectionSubtitle>
                                        Automaticky sa obnovuje každý mesiac. Zrušiť môžete kedykoľvek – prístup vám
                                        zostane do konca zaplateného obdobia.
                                    </SectionSubtitle>
                                    <SubscriptionContainer>
                                        {packages
                                            .filter((pkg) => pkg.isRecurring)
                                            .map((pkg) => (
                                                <PackageCard
                                                    key={pkg.id}
                                                    selected={selectedPackage?.id === pkg.id}
                                                    onClick={() => setSelectedPackage(pkg)}
                                                >
                                                    <PackageName>
                                                        <FaCrown color="#FFD700" />
                                                        {pkg.name}
                                                    </PackageName>
                                                    <PackagePrice>{pkg.price.toFixed(2)} € / mesiac</PackagePrice>
                                                    <PackageDetails>
                                                        Neobmedzený prístup
                                                    </PackageDetails>
                                                    <SubscriptionBadge>
                                                        <FaRedo />
                                                        Automatické obnovovanie mesačne
                                                    </SubscriptionBadge>
                                                </PackageCard>
                                            ))}
                                    </SubscriptionContainer>

                                    {/* The tip compares the two kinds of purchase, so it only has
                                        something to say while both are on sale. */}
                                    {hasOneTimePackages && (
                                        <InfoBox>
                                            <strong>💡 Tip:</strong> Pri jednorazových balíčkoch platíte len raz.
                                            Predplatné sa automaticky obnovuje, ale môžete ho kedykoľvek zrušiť v nastaveniach účtu.
                                        </InfoBox>
                                    )}
                                </PackagesSection>
                            )}
                            <Divider />
                        </ModalStep>
                    )}
                    {currentStep === 1 && (
                        <ModalStep>
                            <StepTitle>Fakturačné Údaje</StepTitle>
                            <Input
                                type="text"
                                placeholder="Celé Meno"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className={missingFields.fullName ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Adresa"
                                value={streetAddress}
                                onChange={(e) => setStreetAddress(e.target.value)}
                                className={missingFields.streetAddress ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="PSČ"
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value)}
                                className={missingFields.postcode ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Mesto"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className={missingFields.city ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Krajina"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className={missingFields.country ? 'input-error' : ''}
                            />
                            {showMissingFieldsMessage && <ErrorMessage>Vyplňte všetky položky</ErrorMessage>}
                        </ModalStep>
                    )}
                    {currentStep === 2 && (
                        <ModalStep>
                            <CenteredContent>
                                <StepTitle>Rekapitulácia</StepTitle>
                                <ProductImage src="/g.png" alt="Góly a Esá" />
                                <CenteredText>{selectedPackage?.name || 'Premium Balíček'}</CenteredText>
                                <CenteredPrice>
                                    Celková cena: {totalPrice.toFixed(2)} EUR
                                </CenteredPrice>
                                {selectedPackage && (
                                    selectedPackage.isRecurring ? (
                                        <SubscriptionBadge style={{ marginTop: '15px', justifyContent: 'center' }}>
                                            <FaRedo />
                                            Predplatné - automaticky sa obnovuje každý mesiac
                                        </SubscriptionBadge>
                                    ) : (
                                        <OneTimeBadge style={{ marginTop: '15px', justifyContent: 'center' }}>
                                            <FaCalendarCheck />
                                            Jednorazová platba - prístup na {formatDuration(selectedPackage.durationDays)}
                                        </OneTimeBadge>
                                    )
                                )}
                            </CenteredContent>
                            <Divider />
                        </ModalStep>
                    )}
                    {currentStep === 3 && (
                        <ModalStep>
                            <StepTitle>Platba</StepTitle>
                            {selectedPackage?.isRecurring && checkout && checkoutState(checkout) !== 'ready' ? (
                                <InfoBox role="status">
                                    {checkoutState(checkout) === 'existing'
                                        ? 'Už máte aktívne členstvo. Novú platbu sme nevytvorili.'
                                        : checkoutState(checkout) === 'paid'
                                            ? 'Platba prebehla. Členstvo sa aktivuje o chvíľu.'
                                            : 'Vaša platba sa ešte spracováva. Ďalšiu platbu sme nevytvorili.'}
                                    {' '}<a href="/predplatne">Skontrolovať členstvo</a>
                                </InfoBox>
                            ) : selectedPackage && (selectedPackage.isRecurring ? checkout?.clientSecret : oneTimeClientSecret) ? (
                                <Elements
                                    key={selectedPackage.isRecurring ? checkout!.clientSecret : oneTimeClientSecret!}
                                    stripe={stripePromise}
                                    options={{
                                        clientSecret: selectedPackage.isRecurring ? checkout!.clientSecret : oneTimeClientSecret!,
                                        appearance: { theme: 'night' },
                                    }}
                                >
                                    <CheckoutPaymentForm
                                        returnUrl={`${window.location.origin}/payment-status?productId=${selectedPackage.id}`}
                                        amount={selectedPackage.isRecurring ? checkout!.amount! : oneTimeAmount}
                                        currency={selectedPackage.isRecurring ? checkout!.currency! : 'eur'}
                                        recurring={selectedPackage.isRecurring}
                                        onError={(message) => setErrorMessage(message || null)}
                                    />
                                </Elements>
                            ) : (
                                <LoadingSpinner>Načítavam platobný formulár...</LoadingSpinner>
                            )}
                        </ModalStep>
                    )}
                    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                </ModalContent>
                <ModalFooter>
                    {currentStep > 0 && <Button onClick={handlePreviousStep} disabled={loadingPaymentIntent}>Späť</Button>}
                    {currentStep < 3 ? (
                        <Button onClick={handleNextStep} disabled={(currentStep === 0 && (!selectedPackage || loadingPackages)) || loadingPaymentIntent}>
                            {loadingPaymentIntent ? 'Načítavam...' : 'Ďalej'}
                        </Button>
                    ) : null}
                </ModalFooter>
            </Modal>
        </Overlay>
    );
};

export default BuyPremium;
